import { Button, TextField, Typography } from "@mui/material";
import { type FormikHelpers, useFormik } from "formik";
import { type FC, useCallback } from "react";
import * as yup from "yup";

import { useSubmitEmailMutation } from "~/feature/auth/authApi";
import { useSignInFormUI } from "~/feature/auth/components/SignUpForm/useSignInFormUI";
import { createEmailValidationSchema } from "~/feature/auth/emailValidationSchema";
import { useRecaptchaToken } from "~/feature/auth/hooks/useRecaptchaToken";
import { recaptchaErrorText } from "~/feature/auth/recaptchaToken";
import { handleError } from "~/utils/handleError";

import * as Styled from "./SignUpForm.styles";

enum FieldName {
  Email = "email",
  RecaptchaToken = "recaptchaToken",
}

type SignUpFormValues = {
  email: string;
  recaptchaToken: string;
};

const initialValues: SignUpFormValues = {
  email: "",
  recaptchaToken: "",
};

const schema = yup.object({
  [FieldName.Email]: createEmailValidationSchema(),
});

interface ISignUpFormProps {
  onSuccess: () => void;
  small?: boolean;
  stacked?: boolean;
  buttonLabel?: string;
}

export const SignUpForm: FC<ISignUpFormProps> = ({
  onSuccess,
  small = false,
  stacked = false,
  buttonLabel,
}) => {
  const requestRecaptchaToken = useRecaptchaToken();
  const [submitEmail] = useSubmitEmailMutation();
  const { buttonName, inputSize, inputSlotProps, inputLabel } =
    useSignInFormUI(stacked ? true : small, buttonLabel);

  const submitForm = useCallback(
    async (
      values: SignUpFormValues,
      helpers: FormikHelpers<SignUpFormValues>
    ) => {
      try {
        const recaptcha = await requestRecaptchaToken("signup");

        if (!recaptcha.ok) {
          helpers.setFieldError(
            FieldName.RecaptchaToken,
            recaptchaErrorText(recaptcha.reason)
          );
          return;
        }

        helpers.setFieldError(FieldName.RecaptchaToken, "");

        await submitEmail({
          ...values,
          recaptchaToken: recaptcha.token,
          source: "site_button",
        }).unwrap();
        onSuccess();
      } catch (error) {
        handleError(error);
      } finally {
        helpers.setSubmitting(false);
        helpers.resetForm();
      }
    },
    [requestRecaptchaToken, submitEmail, onSuccess]
  );

  const formik = useFormik<SignUpFormValues>({
    initialValues,
    validationSchema: schema,
    validateOnBlur: !small && !stacked,
    validateOnChange: false,
    onSubmit: async (values, helpers) => submitForm(values, helpers),
  });

  const showRecaptchaError =
    formik.submitCount > 0 && Boolean(formik.errors.recaptchaToken);

  return (
    <Styled.Form onSubmit={formik.handleSubmit} $small={small} $stacked={stacked}>
      <Styled.Wrapper>
        <TextField
          type="email"
          name={FieldName.Email}
          label={inputLabel}
          variant="outlined"
          placeholder="example@gmail.com"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          error={Boolean(formik.touched.email && formik.errors.email)}
          helperText={formik.touched.email ? formik.errors.email : undefined}
          autoComplete="email"
          fullWidth
          size={inputSize}
          slotProps={inputSlotProps}
        />
        {showRecaptchaError ? (
          <Typography variant="caption" color="error">
            {formik.errors.recaptchaToken}
          </Typography>
        ) : null}
      </Styled.Wrapper>
      <Button
        variant="contained"
        size="small"
        type="submit"
        loading={formik.isSubmitting}
        disabled={formik.isSubmitting}
        fullWidth={!small || stacked || Boolean(buttonLabel)}
        sx={{ flexShrink: 0 }}
      >
        <Typography
          sx={{
            textTransform: "none",
            fontSize: 14
          }}>
          {buttonName}
        </Typography>
      </Button>
    </Styled.Form>
  );
};
